import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getBirthInfoFromKeyFact } from '../../../../models/BirthdayUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { Input } from '../../../components-v2/Input';
import { bs } from '../../../styles/styles';
import { getKeyFactOptions, SplitDatePicker, validateDateForKeyFacts } from './ContactInfoSmartKeyFactsModal';

/**
 * Key-fact kinds the edit modal supports: date-based kinds where editing the date (and, for
 * birthdays, the name) is meaningful. Deliberately excludes Renewal/policy (own flow), the
 * Turning65/72/73/XX milestone variants (derived, not directly editable here), and free-text facts.
 */
const EDITABLE_KEY_DATE_KINDS = new Set<string>([
	Api.KeyDateKind.Birthday,
	Api.KeyDateKind.Anniversary,
	Api.KeyDateKind.Wedding,
	Api.KeyDateKind.FinancialReview,
	Api.KeyDateKind.Custom1,
	Api.KeyDateKind.Custom2,
	Api.KeyDateKind.Custom3,
	Api.KeyDateKind.Custom4,
	Api.KeyDateKind.Custom5,
	Api.KeyDateKind.Custom6,
	Api.KeyDateKind.Custom7,
	Api.KeyDateKind.Custom8,
	Api.KeyDateKind.Custom9,
	Api.KeyDateKind.Custom10,
]);

/** Whether a key fact should be edited via {@link EditKeyFactModalContent} (vs. inline / not at all). */
export const isEditableViaKeyFactModal = (keyFact: Api.IKeyFact): boolean =>
	!!keyFact.keyDate?.kind && EDITABLE_KEY_DATE_KINDS.has(keyFact.keyDate.kind);

export interface IEditKeyFactPayload {
	keyFactId: string;
	/** Polymorphic key-fact metadata, mirroring the create payload (selected by `_type`). */
	keyFactMetaData: Api.IKeyFactMetaData;
	/** When true, the year is dropped from the displayed date. */
	ignoreYear: boolean;
}

interface Props {
	compactLayout: boolean;
	contact: Api.IContact;
	keyFact: Api.IKeyFact;
	onClose: () => void;
	/**
	 * Persists the edit. Until the contact key-fact update endpoint exists this is omitted and the
	 * Save button is disabled — the modal still opens and pre-populates so the flow can be reviewed.
	 */
	onSave?: (payload: IEditKeyFactPayload) => void;
	isSaving?: boolean;
}

/** Pulls the ISO date string out of whichever date-bearing metadata the key fact carries. */
const getMetadataIsoDate = (keyFact: Api.IKeyFact): string | null => {
	const metadata = keyFact.source?.additionalMetadata;
	if (!metadata) {
		return null;
	}
	if ('birthInfo' in metadata && metadata.birthInfo?.date) {
		return metadata.birthInfo.date;
	}
	if ('date' in metadata && metadata.date) {
		return metadata.date;
	}
	return null;
};

export function EditKeyFactModalContent({ keyFact, contact, onClose, onSave, isSaving = false, compactLayout }: Props) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	// Only the exact Birthday kind gets the name field + BirthInfoKeyFactMetadata. (isBirthdayKeyFact
	// also matches the Turning65/72/73/XX milestone variants, which this modal doesn't handle.)
	const isBirthday = keyFact.keyDate?.kind === Api.KeyDateKind.Birthday;
	const birthInfo = getBirthInfoFromKeyFact(keyFact);

	const policyBoardEnabled = userSession.account?.features?.boards?.policiesEnabled;
	// Reuse the create modal's option list so the locked type label matches exactly (including
	// account-configured custom date-kind names).
	const typeLabel = React.useMemo(() => {
		const options = getKeyFactOptions(userSession.account.preferences, contact, policyBoardEnabled);
		return (
			options.find(option => option.dataContext === keyFact.keyDate?.kind)?.text ?? keyFact.keyDate?.kind ?? 'Key Fact'
		);
	}, [userSession.account.preferences, contact, policyBoardEnabled, keyFact.keyDate?.kind]);

	const initialYear = React.useMemo(() => {
		const isoDate = getMetadataIsoDate(keyFact);
		if (!isoDate) {
			return null;
		}
		const parsed = new Date(isoDate);
		return Number.isNaN(parsed.getTime()) ? null : parsed.getUTCFullYear();
	}, [keyFact]);

	const [day, setDay] = React.useState<number | null>(keyFact.keyDate?.day ?? null);
	const [month, setMonth] = React.useState<number | null>(keyFact.keyDate?.month ?? null);
	const [year, setYear] = React.useState<number | null>(initialYear);
	const [name, setName] = React.useState(birthInfo?.name ?? '');

	const handleSave = () => {
		if (!onSave) {
			return;
		}
		const validationError = validateDateForKeyFacts({ day, month, year });
		if (validationError) {
			errorMessages.push({ messages: [validationError] });
			return;
		}
		// Build the same metadata the create flow sends so the server re-derives value/keyDate/birthInfo
		// consistently. Use Date.UTC so the calendar date is preserved regardless of the user's timezone.
		const isoDate = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)).toISOString();
		const keyFactMetaData: Api.IKeyFactMetaData = isBirthday
			? ({
					_type: Api.IKeyFactType.BirthInfoKeyFactMetadata,
					birthInfo: { date: isoDate, name: name.trim() },
				} as Api.IBirthInfoKeyFactMetadata)
			: ({
					_type: Api.IKeyFactType.CustomerReferenceDateKeyFactMetadata,
					date: isoDate,
					keyDateKind: keyFact.keyDate?.kind,
				} as Api.ICustomerReferenceDateKeyFactMetadata);
		onSave({ keyFactId: keyFact.id, keyFactMetaData, ignoreYear: !year });
	};

	return (
		<div className={css(!compactLayout && styleSheet.container)}>
			<header className={css(bs.pb5, bs.textBrandSecondary, bs.textLg)}>Edit Key Fact</header>
			<div className={css(bs.flex, bs.flexCol, bs.gap4, bs.mb4)}>
				<div>
					<label className={css(bs.textLabel, bs.block, bs.textSm, bs.mb1)}>Key Fact Type</label>
					{/* Type is fixed on edit — shown read-only. */}
					<Input value={typeLabel} disabled styles={[bs.wFull, bs.maxWFull]} />
				</div>
				<SplitDatePicker
					day={day}
					month={month}
					year={year}
					onChangeDay={setDay}
					onChangeMonth={setMonth}
					onChangeYear={setYear}
					yearPlaceholder='Optional'
				/>
				{isBirthday ? (
					<div>
						<label className={css(bs.textLabel, bs.block, bs.textSm, bs.mb1)} htmlFor='edit-birthday-person-name'>
							Whose birthday is it? (Optional)
						</label>
						<Input id='edit-birthday-person-name' value={name} onChange={ev => setName(ev.target.value)} />
					</div>
				) : null}
				{isBirthday ? (
					<div>
						<label className={css(bs.textLabel, bs.block, bs.textSm, bs.mb1)} htmlFor='edit-keyfact-value'>
							Display Text
						</label>
						<Input id='edit-keyfact-value' value={keyFact.value ?? ''} disabled styles={[bs.wFull]} />
						<span className={css(bs.textSm, bs.block, bs.mt1, styleSheet.note)}>
							The current stored display text. If it differs from the name or date above, it will be corrected when you
							save.
						</span>
					</div>
				) : null}
			</div>
			<div className={css(bs.flex, bs.gap2)}>
				<Button variant='primary' onClick={handleSave} disabled={!onSave} isLoading={isSaving}>
					Save
				</Button>
				<Button variant='outline' onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
			</div>
		</div>
	);
}

const styleSheet = StyleSheet.create({
	container: {
		width: 420,
	},
	note: {
		color: THEME.colors.text.light,
	},
});
